// Format currency helper
import {ValueSummary} from "@shared/interfaces/valueAssessment.interface.ts";
import {CalculateInvestmentBreakdownFn} from "@/hooks/usePricingData.ts";
import {PricingData} from "@shared/interfaces/pricingData.interface.ts";
import {InvestmentBreakdown} from "@shared/interfaces/investmentBreakdown.interface.ts";
import {DiscountSummary} from "@shared/interfaces/discountSummary.interface.ts";

export const formatCurrency = (value: number | string | undefined): string => {
    if (!value || value === 0) return "—"
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return "—"

    // Handle negative numbers by preserving the sign
    const isNegative = numValue < 0
    const absValue = Math.abs(numValue)

    // Format in millions (M) for values >= 1,000,000, thousands (K) for values >= 1,000
    if (absValue >= 1000000) {
        const mValue = absValue / 1000000
        return `${isNegative ? '-' : ''}$${mValue.toFixed(2)}M`
    } else if (absValue >= 1000) {
        const kValue = absValue / 1000
        return `${isNegative ? '-' : ''}$${kValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}K`
    }
    return `${isNegative ? '-' : ''}$${absValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

// Format percentage helper
export const formatPercentage = (value: number | string | undefined): string => {
    if (!value || value === 0) return "—"
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return "—"
    return `${numValue.toFixed(0)}%`
}

// Format months helper
export const formatMonths = (value: number | string | undefined): string => {
    if (!value || value === 0) return "—"
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(numValue)) return "—"
    return `${numValue.toFixed(1)} months`
}

// This is the function type:

export function buildInvestmentBreakdown({
                                             revenueBand,
                                             selectedPillars,
                                             entityCount,
                                             discountSoftwarePercent = 0,
                                             discountServicesPercent = 0,
                                             pricingData,
                                             calculatePricingBreakdown,
                                             valueSummary
                                         }: {
    revenueBand: string;
    selectedPillars: string[];
    entityCount: number;
    discountSoftwarePercent?: number;
    discountServicesPercent?: number;
    pricingData: PricingData | undefined;
    calculatePricingBreakdown: CalculateInvestmentBreakdownFn;
    valueSummary: ValueSummary;
}): InvestmentBreakdown {


    // First, try to use the pricing hook data if available
    if (pricingData) {
        const pricingBreakdown = calculatePricingBreakdown(
            revenueBand,
            selectedPillars,
            entityCount,
            pricingData
        )

        if (pricingBreakdown) {
            // Get entity base price from pricing data - NO HARDCODED VALUES
            const entityBasePriceEntry = pricingData.platform.find(p => p.revenue_band === revenueBand)
            const basePricePerEntity = entityBasePriceEntry?.entity_base_price ? Number(entityBasePriceEntry.entity_base_price) : 7500

            // Apply discount sliders to software costs
            const discountedAnnualSubscription = pricingBreakdown.annualSubscription * (1 - discountSoftwarePercent)

            // ADJUSTED LOGIC: Year 1 Software = 5x annual, Services = 2x annual
            const year1SoftwareCost = discountedAnnualSubscription * 5  // 5-year software investment
            const implementationServicesCost = discountedAnnualSubscription * 2
            const discountedServicesCost = implementationServicesCost * (1 - discountServicesPercent)

            // Convert the pricing breakdown to our investment breakdown format with discounts
            const annualRecurringCosts = discountedAnnualSubscription
            const year1SetupCosts = discountedServicesCost  // 2x annual software for implementation
            const year1Investment = year1SoftwareCost + year1SetupCosts  // 5x annual + 2x annual = 7x annual
            const year2to5Investment = annualRecurringCosts * 4
            const totalFiveYearInvestment = year1Investment + year2to5Investment

            return {
                platform: pricingBreakdown.platformCost,
                totalPillars: pricingBreakdown.pillarCost,
                pillars: selectedPillars.map((pillar: string) => ({
                    name: pillar,
                    cost: pricingBreakdown.pillarCostPerUnit || (pricingBreakdown.pillarCost / selectedPillars.length)
                })),
                annualRecurringCosts: annualRecurringCosts,
                entityPricePerYear: basePricePerEntity, // Database-driven entity base price
                baseEntityCosts: pricingBreakdown.entityCount * basePricePerEntity,
                discountedEntityCosts: pricingBreakdown.entityCost,
                entityDiscountAmount: (pricingBreakdown.entityCount * basePricePerEntity) - pricingBreakdown.entityCost,
                year1Investment: year1Investment,
                year1SetupCosts: year1SetupCosts,
                year2to5Investment: year2to5Investment,
                finalPrice: totalFiveYearInvestment,
                listPrice: pricingBreakdown.entityCount * basePricePerEntity,
                entityDiscount: (pricingBreakdown.entityCount * basePricePerEntity) - pricingBreakdown.entityCost,
                entityCount: entityCount,
                revenueBand: revenueBand,
                discountPercentage: Math.round(((pricingBreakdown.entityCount * basePricePerEntity - pricingBreakdown.entityCost) / (pricingBreakdown.entityCount * basePricePerEntity)) * 100) + '%',

                // Add original costs for discount display
                originalAnnualSubscription: pricingBreakdown.annualSubscription,
                originalServicesCost: pricingBreakdown.servicesCost,
                discountedAnnualSubscription: discountedAnnualSubscription,
                discountedServicesCost: discountedServicesCost,
                year1SoftwareCost: year1SoftwareCost  // For display purposes
            }
        }
    }


    // TODO There is no fallback like this!!! there is no investmentBreakdown anywhere.
    // Fallback: try to extract from valueSummary investment data
    const investmentData = valueSummary?.roiData?.investmentBreakdown || {}

    if (investmentData.platformCost) {
        // Apply discounts to existing investment data
        const originalAnnualRecurring = investmentData.annualRecurringCosts || 0
        const originalSetupCosts = investmentData.year1SetupCosts || 0

        const discountedAnnualRecurring = originalAnnualRecurring * (1 - discountSoftwarePercent)
        const discountedSetupCosts = originalSetupCosts * (1 - discountServicesPercent)

        console.log('📊 Using existing investment data from valueSummary with discounts:', {
            originalAnnualRecurring,
            originalSetupCosts,
            discountSoftwarePercent: `${(discountSoftwarePercent * 100).toFixed(0)}%`,
            discountServicesPercent: `${(discountServicesPercent * 100).toFixed(0)}%`,
            discountedAnnualRecurring,
            discountedSetupCosts
        })

        return {
            ...investmentData,
            annualRecurringCosts: discountedAnnualRecurring,
            year1SetupCosts: discountedSetupCosts,
            year1Investment: discountedAnnualRecurring + discountedSetupCosts,
            year2to5Investment: discountedAnnualRecurring * 4,
            finalPrice: (discountedAnnualRecurring + discountedSetupCosts) + (discountedAnnualRecurring * 4)
        }
    }

    // Last resort: return minimal structure with zeros
    // console.warn('⚠️ No pricing data available - returning minimal structure')
    return {
        platform: 0,
        totalPillars: 0,
        pillars: selectedPillars.map((pillar: string) => ({ name: pillar, cost: 0 })),
        annualRecurringCosts: 0,
        entityPricePerYear: 2500,
        baseEntityCosts: entityCount * 2500,
        discountedEntityCosts: entityCount * 2500,
        entityDiscountAmount: 0,
        year1Investment: 0,
        year1SetupCosts: 0,
        year2to5Investment: 0,
        finalPrice: 0,
        listPrice: entityCount * 2500,
        entityDiscount: 0,
        entityCount: entityCount,
        revenueBand: revenueBand,
        discountPercentage: '0%'
    }
}

/**
 * Compute subscription/services discount metrics from an InvestmentBreakdown.
 * Falls back to:
 * - year1SetupCosts for services when original/discounted services aren't provided
 * - annualRecurringCosts for subscription when discounted subscription isn't provided
 * - undefined for subscriptionOriginal when original subscription isn't provided
 */
export function computeDiscountsFromInvestmentBreakdown(
    investmentBreakdown: InvestmentBreakdown
): DiscountSummary {
    const servicesOriginal =
        typeof investmentBreakdown.originalServicesCost === 'number'
            ? investmentBreakdown.originalServicesCost
            : investmentBreakdown.year1SetupCosts;

    const subscriptionOriginal =
        typeof investmentBreakdown.originalAnnualSubscription === 'number'
            ? investmentBreakdown.originalAnnualSubscription
            : undefined;

    const subscriptionDiscounted =
        typeof investmentBreakdown.discountedAnnualSubscription === 'number'
            ? investmentBreakdown.discountedAnnualSubscription
            : investmentBreakdown.annualRecurringCosts;

    const servicesDiscounted =
        typeof investmentBreakdown.discountedServicesCost === 'number'
            ? investmentBreakdown.discountedServicesCost
            : investmentBreakdown.year1SetupCosts;

    const servicesDiscountAmount = Math.max(0, servicesOriginal - servicesDiscounted);
    const servicesDiscountPercent =
        servicesOriginal > 0 ? Math.round((servicesDiscountAmount / servicesOriginal) * 100) : 0;

    const subscriptionDiscountAmount =
        subscriptionOriginal !== undefined
            ? Math.max(0, subscriptionOriginal - subscriptionDiscounted)
            : 0;

    const subscriptionDiscountPercent =
        subscriptionOriginal && subscriptionOriginal > 0
            ? Math.round((subscriptionDiscountAmount / subscriptionOriginal) * 100)
            : 0;

    return {
        servicesOriginal,
        servicesDiscounted,
        subscriptionOriginal,
        subscriptionDiscounted,
        servicesDiscountAmount,
        subscriptionDiscountAmount,
        servicesDiscountPercent,
        servicesDiscountPercentLabel: `${servicesDiscountPercent}%`,
        subscriptionDiscountPercent,
        subscriptionDiscountPercentLabel: `${subscriptionDiscountPercent}%`,
    };
}
